import { useEffect, useRef, useState } from 'react';
import { Box, Loader2 } from 'lucide-react';
import { DbSwitch } from './DbSwitch';
import { mountMesh } from './meshScene';
import { useLanguage } from '../../contexts/LanguageContext';
import { PICKED } from './pickedLook';

// One run, as the mesh it produced rather than as its number.
//
// It replaces a row of pills reading #13 #12 #10, which named the runs without saying
// anything about them: choosing between two reconstructions of the same part meant
// opening each in turn and remembering the last. Drawn side by side they are simply
// comparable, which is the entire reason the older runs are kept.
//
// Not interactive, deliberately. Every thumbnail frames its object the same way (see
// meshScene), so the row is a fair comparison and not several objects at several angles;
// and a grid of canvases that each grabbed the wheel would make the page unscrollable.
// Turning one is what the viewer above is for, one click away.
export function MeshThumb({
  src, selected, inCatalog, label, title, onSelect, onToggleCatalog, catalogBusy = false,
  catalogFixed, working = false,
}: {
  // A RUN STILL GOING: the pass it has landed, under a spinner — one tile for the run, not one for
  // the pass and another for the wait.
  working?: boolean;
  src: string;
  selected: boolean;
  // In the Parts DB. Marked with the icon a kept picture is marked with, in the same
  // corner, because it is the same fact about a different asset.
  inCatalog: boolean;
  label: string;
  title?: string;
  onSelect: () => void;
  // Put this mesh in the Parts DB, or take it out. Given, the mark is drawn in BOTH states
  // and is the switch; withheld, there is no mark at all — which is what the catalog's own
  // table wants, every mesh in it being in the database by definition.
  onToggleCatalog?: (inCatalog: boolean) => void;
  catalogBusy?: boolean;
  // Why its switch is locked, where it is — see CatalogSwitch.fixed.
  catalogFixed?: string;
}) {
  const { t } = useLanguage();
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  // Whether this tile is on screen. A thumbnail is one WebGL context, and a browser
  // grants about sixteen before it starts dropping the oldest — which is not an error,
  // it is a canvas that silently goes blank. The Parts DB is a long table with a mesh
  // cell per part, so mounting them all would blank most of them; they are built when
  // they scroll in and taken down when they leave, which bounds the count by the
  // viewport instead of by the size of the database.
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    // A margin, so a tile is drawn just before it is looked at rather than popping in.
    const io = new IntersectionObserver(
      ([entry]) => setNear(entry.isIntersecting),
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = host.current;
    if (!el || !near) return;
    setState('loading');
    // Follows the shared angle: not interactive, so it takes its aim from whichever
    // viewer has a mouse on it and turns with it. A strip showing a side you have
    // already decided is the uninteresting one is a strip that compares nothing.
    return mountMesh(el, src, {
      onState: setState,
      sharedView: true,
      // IN TURN WITH THE OTHER TILES. A strip is four or five of these mounting in the same
      // frame, and a GLB is parsed on the main thread — a hundred megabytes of chair, four
      // times over, is a page that does not scroll while it lands. See meshScene's queue.
      queued: true,
      // A gizmo, without its labels: at a third of a 96-pixel tile the colours are
      // legible and nothing written is.
      axes: true,
    });
  }, [src, near]);

  return (
    <figure className="flex w-24 shrink-0 flex-col items-center gap-1">
      {/* The mark is a button of its own, so it is a SIBLING of the tile rather than
          inside it: a button cannot hold a button, and the click that keeps a mesh must
          not also be the click that opens it in the viewer. */}
      <div className="relative">
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          title={title}
          // White in both themes, as the viewer is and for the same reason: the meshes are
          // dark wood, and on the dark theme's navy a thumbnail was a silhouette.
          className={`relative block h-24 w-24 overflow-hidden rounded-xl border-2 transition-colors ${
            selected
              ? PICKED
              : 'border-slate-200 bg-white hover:border-blue-400'
          }`}
        >
          <div ref={host} className="h-full w-full" />

          {working && (
            <span className="absolute inset-0 flex items-center justify-center bg-white/60">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </span>
          )}
          {state === 'loading' && near && !working && (
            <span className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
            </span>
          )}
          {/* The file may well be fine — this says the PREVIEW failed, and the viewer above
              is where that gets diagnosed. */}
          {state === 'failed' && (
            <span className="absolute inset-0 flex items-center justify-center">
              <Box className="h-5 w-5 text-slate-300" />
            </span>
          )}
        </button>

        {/* In the Parts DB — the mark AND the switch, on every run rather than on the one
            open in the viewer. It was a green badge drawn only on the runs already in, with
            the control for it a button under the viewer: to keep a mesh you opened it
            first, then read a button somewhere else to find out what state it was in.

            Drawn in both states, so what is left to do is countable down the row: grey
            means not in the database, and clicking it puts it there. */}
        {onToggleCatalog && (
          <DbSwitch
            on={inCatalog}
            busy={catalogBusy}
            disabled={!!catalogFixed}
            title={catalogFixed ?? t(inCatalog ? 'notice.3d.inDb' : 'notice.3d.addToDb')}
            className="absolute left-1 top-1"
            onToggle={() => onToggleCatalog(inCatalog)}
          />
        )}
      </div>
      <figcaption
        className={`text-center text-[11px] font-medium ${
          selected ? 'text-blue-600' : 'text-slate-400'
        }`}
      >
        {label}
      </figcaption>
    </figure>
  );
}
